import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell, Check, X, Edit3, DollarSign, TrendingUp, TrendingDown,
  Clock, AlertTriangle, CheckCircle, XCircle, Settings, RefreshCw,
  ChevronDown, ChevronUp, Truck, Utensils, Zap, History, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Badge component for status
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    approved: 'bg-green-100 text-green-800 border-green-200',
    rejected: 'bg-red-100 text-red-800 border-red-200',
    modified: 'bg-blue-100 text-blue-800 border-blue-200',
    auto_approved: 'bg-purple-100 text-purple-800 border-purple-200'
  };
  
  const icons = {
    pending: Clock,
    approved: CheckCircle,
    rejected: XCircle,
    modified: Edit3,
    auto_approved: Zap
  };
  
  const Icon = icons[status] || Clock;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
      <Icon className="w-3 h-3" />
      {status?.replace('_', ' ')}
    </span>
  );
};

// Alert Card Component
const AlertCard = ({ alert, onApprove, onReject, onModify, isProcessing }) => {
  const [showModify, setShowModify] = useState(false);
  const [newPrice, setNewPrice] = useState(alert.recommended_price);
  const [expanded, setExpanded] = useState(false);
  
  const isIncrease = alert.change_percent > 0;
  const isPending = alert.status === 'pending';
  
  return (
    <div className={`bg-white rounded-xl border ${isPending ? 'border-yellow-200 shadow-sm' : 'border-gray-200'} overflow-hidden transition-all`}>
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              alert.category === 'roadside' ? 'bg-blue-100' : 'bg-orange-100'
            }`}>
              {alert.category === 'roadside' ? (
                <Truck className="w-5 h-5 text-blue-600" />
              ) : (
                <Utensils className="w-5 h-5 text-orange-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{alert.item_name}</h3>
              <p className="text-sm text-gray-500 capitalize">{alert.category}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Price Change */}
            <div className="text-right">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm line-through">${alert.current_price?.toFixed(2)}</span>
                <span className="font-bold text-gray-900">${alert.recommended_price?.toFixed(2)}</span>
              </div>
              <div className={`flex items-center justify-end gap-1 text-sm ${
                isIncrease ? 'text-red-600' : 'text-green-600'
              }`}>
                {isIncrease ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {isIncrease ? '+' : ''}{alert.change_percent?.toFixed(1)}%
              </div>
            </div>
            
            <StatusBadge status={alert.status} />
            
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="pt-4 space-y-4">
            {/* AI Reasoning */}
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-purple-800">AI Reasoning</span>
                <span className="text-sm text-purple-600">({alert.confidence}% confidence)</span>
              </div>
              <p className="text-sm text-purple-700">{alert.reason}</p>
            </div>
            
            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {new Date(alert.created_at).toLocaleString()}
              </span>
              {alert.processed_by_name && (
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  Processed by {alert.processed_by_name}
                </span>
              )}
            </div>
            
            {/* Actions for pending alerts */}
            {isPending && (
              <div className="flex flex-wrap gap-3 pt-2">
                {!showModify ? (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); onApprove(alert.alert_id); }}
                      disabled={isProcessing}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      data-testid={`approve-alert-${alert.alert_id}`}
                    >
                      <Check className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onReject(alert.alert_id); }}
                      disabled={isProcessing}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                      data-testid={`reject-alert-${alert.alert_id}`}
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowModify(true); }}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid={`modify-alert-${alert.alert_id}`}
                    >
                      <Edit3 className="w-4 h-4" />
                      Modify
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-500" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newPrice}
                        onChange={(e) => setNewPrice(parseFloat(e.target.value))}
                        onClick={(e) => e.stopPropagation()}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        data-testid={`modify-price-input-${alert.alert_id}`}
                      />
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); onModify(alert.alert_id, newPrice); }}
                      disabled={isProcessing || !newPrice}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      Apply
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowModify(false); }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Dashboard Component
export default function PriceAlertsDashboard() {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total_pending: 0, roadside_pending: 0, menu_pending: 0 });
  const [history, setHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState({ approved: 0, rejected: 0, modified: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  
  const fetchAlerts = useCallback(async () => {
    try {
      const [pendingRes, historyRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/price-alerts/pending`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/price-alerts/history?limit=50`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/price-alerts/settings`, { headers: getAuthHeaders() })
      ]);
      
      setAlerts(pendingRes.data.alerts || []);
      setStats(pendingRes.data.stats || {});
      setHistory(historyRes.data.alerts || []);
      setHistoryStats(historyRes.data.stats || {});
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load price alerts');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);
  
  const handleApprove = async (alertId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/price-alerts/approve/${alertId}`, {}, { headers: getAuthHeaders() });
      toast.success('Price change approved and applied');
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async (alertId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/price-alerts/reject/${alertId}`, { reason: 'Rejected by admin' }, { headers: getAuthHeaders() });
      toast.success('Price recommendation rejected');
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleModify = async (alertId, newPrice) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/price-alerts/modify/${alertId}`, { new_price: newPrice }, { headers: getAuthHeaders() });
      toast.success('Price modified and applied');
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to modify');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleBulkApprove = async () => {
    if (!confirm('Are you sure you want to approve all pending price changes?')) return;
    setProcessing(true);
    try {
      const category = categoryFilter !== 'all' ? categoryFilter : undefined;
      const res = await axios.post(`${API}/api/price-alerts/approve-all`, { category }, { headers: getAuthHeaders() });
      toast.success(`Approved ${res.data.approved} price changes`);
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk approve');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleBulkReject = async () => {
    if (!confirm('Are you sure you want to reject all pending price changes?')) return;
    setProcessing(true);
    try {
      const category = categoryFilter !== 'all' ? categoryFilter : undefined;
      const res = await axios.post(`${API}/api/price-alerts/reject-all`, { category, reason: 'Bulk rejected' }, { headers: getAuthHeaders() });
      toast.success(`Rejected ${res.data.rejected} price changes`);
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reject');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/api/price-alerts/settings`, settings, { headers: getAuthHeaders() });
      toast.success('Settings saved');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };
  
  const filteredAlerts = alerts.filter(a => categoryFilter === 'all' || a.category === categoryFilter);
  const filteredHistory = history.filter(a => categoryFilter === 'all' || a.category === categoryFilter);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="price-alerts-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Price Alerts</h1>
                <p className="text-gray-500">Review AI price recommendations before they're applied</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchAlerts()}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Alerts</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.total_pending}</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Roadside</p>
                <p className="text-2xl font-bold text-blue-600">{stats.roadside_pending}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Menu Items</p>
                <p className="text-2xl font-bold text-orange-600">{stats.menu_pending}</p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Utensils className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Processed</p>
                <p className="text-2xl font-bold text-green-600">{historyStats.approved + historyStats.rejected + historyStats.modified}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <History className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Settings Panel */}
        {showSettings && settings && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Auto-Approve Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Enable Auto-Approve</p>
                  <p className="text-sm text-gray-500">Automatically apply high-confidence recommendations</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.auto_approve_enabled}
                    onChange={(e) => setSettings({...settings, auto_approve_enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              <div>
                <label className="block font-medium text-gray-900 mb-2">Confidence Threshold (%)</label>
                <input
                  type="number"
                  min="50"
                  max="100"
                  value={settings.auto_approve_threshold}
                  onChange={(e) => setSettings({...settings, auto_approve_threshold: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block font-medium text-gray-900 mb-2">Max Auto Change (%)</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={settings.max_auto_change_percent}
                  onChange={(e) => setSettings({...settings, max_auto_change_percent: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleSaveSettings}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Tabs & Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'pending' 
                  ? 'bg-yellow-100 text-yellow-800' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Pending ({stats.total_pending})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'history' 
                  ? 'bg-gray-200 text-gray-800' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              History
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                <option value="roadside">Roadside</option>
                <option value="menu">Menu Items</option>
              </select>
            </div>
            
            {activeTab === 'pending' && filteredAlerts.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={handleBulkApprove}
                  disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  <Check className="w-4 h-4" />
                  Approve All
                </button>
                <button
                  onClick={handleBulkReject}
                  disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Reject All
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Alerts List */}
        <div className="space-y-4">
          {activeTab === 'pending' ? (
            filteredAlerts.length > 0 ? (
              filteredAlerts.map((alert) => (
                <AlertCard
                  key={alert.alert_id}
                  alert={alert}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onModify={handleModify}
                  isProcessing={processing}
                />
              ))
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
                <p className="text-gray-500">No pending price alerts to review. Check back after the next scheduled run.</p>
              </div>
            )
          ) : (
            filteredHistory.length > 0 ? (
              filteredHistory.map((alert) => (
                <AlertCard
                  key={alert.alert_id}
                  alert={alert}
                  onApprove={() => {}}
                  onReject={() => {}}
                  onModify={() => {}}
                  isProcessing={false}
                />
              ))
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No History Yet</h3>
                <p className="text-gray-500">Processed alerts will appear here.</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
